import { Component, effect, inject, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatDialog } from "@angular/material/dialog";
import { Subject, catchError, of, takeUntil, tap } from "rxjs";
import dayjs from "dayjs";

import { EmptyComponent } from "../../../shared/components/empty/empty.component";
import { GloControlComponent } from "../../../shared/components/glo-control/glo-control.component";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { GloPaginationComponent } from "../../../shared/components/pagination/pagination.component";
import { BusinessDiscountAddComponent } from "../discount-add/discount-add.component";
import { ApiService } from "../../../shared/services/api/api.service";
import { ApiResponse } from "../../../shared/models/shared.model";
import { API_ENDPOINTS } from "../../../shared/services/api/api-endpoints";
import { promoVoucher } from "../../../shared/models/promo.model";
import { BusinessSalonContextService } from "../../../shared/services/api/business-salon-context.service";

@Component({
  selector: "business-promotion",
  standalone: true,
  imports: [
    CommonModule,
    EmptyComponent,
    GloControlComponent,
    LoaderComponent,
    GloPaginationComponent,
  ],
  templateUrl: "./promotion.component.html",
  styleUrl: "./promotion.component.scss",
})
export class BusinessPromotionComponent implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);
  private destroy$ = new Subject<void>();

  readonly salonContext = inject(BusinessSalonContextService);

  isLoading = false;

  promoVouchers: any[] = [];
  filteredPromoVouchers: any[] = [];
  viewPromoVouchers: any[] = [];

  currentPage = 1;
  itemsPerPage = 10;

  private searchTerm = "";

  constructor(private apiService: ApiService) {
    /*
     * Reload promotions whenever the global sidebar salon selection changes.
     *
     * BusinessComponent initializes the salon context. Waiting for salons to
     * exist prevents an initial unscoped API request.
     */
    effect(() => {
      const salons = this.salonContext.salons();
      this.salonContext.selectedSalonIds();

      if (!salons.length) {
        return;
      }

      this.getPromoVouchers().pipe(takeUntil(this.destroy$)).subscribe();
    });
  }

  ngOnInit(): void {
    /*
     * Loading is handled by the salon-context effect.
     */
  }

  /* =========================================================
     Add / Edit
     ========================================================= */

  addDiscount(): void {
    const dialogRef = this.dialog.open(BusinessDiscountAddComponent, {
      panelClass: ["crm-detail-dialog"],

      data: {
        promoId: "",

        /*
         * The create component can use this to default or limit its
         * salon selection to the current global scope.
         */
        selectedSalonIds: this.salonContext.selectedSalonIds(),
      },

      width: "760px",
      maxWidth: "100vw",
      height: "100vh",

      position: {
        right: "0",
        top: "0",
      },
    });

    dialogRef.afterClosed().subscribe((refresh) => {
      if (refresh) {
        this.onclose();
      }
    });
  }

  editDiscount(voucher: promoVoucher): void {
    if (!voucher.promotion_id) {
      return;
    }

    const dialogRef = this.dialog.open(BusinessDiscountAddComponent, {
      panelClass: ["crm-detail-dialog"],

      data: {
        promoId: voucher.promotion_id,
        promoData: voucher,

        selectedSalonIds: this.salonContext.selectedSalonIds(),
      },

      width: "760px",
      maxWidth: "100vw",
      height: "100vh",

      position: {
        right: "0",
        top: "0",
      },
    });

    dialogRef.afterClosed().subscribe((refresh) => {
      if (refresh) {
        this.onclose();
      }
    });
  }

  /* =========================================================
     Load promotions
     ========================================================= */

  getPromoVouchers() {
    this.isLoading = true;

    const url = this.salonContext.withSalonScope(
      API_ENDPOINTS.owner.promoCode.list,
    );

    return this.apiService.request<ApiResponse<[]>>(url, "GET").pipe(
      tap((response: any) => {
        const results =
          response?.results ?? response?.data?.results ?? response?.data ?? [];

        this.promoVouchers = Array.isArray(results) ? results : [];

        /*
         * Keep the current search term when the salon scope changes.
         */
        this.applySearchFilter(false);

        this.isLoading = false;
      }),

      catchError((error) => {
        console.error("Error loading promotions:", error);

        /*
         * Clear stale data so promotions from the previously selected
         * salon scope never remain visible after a failed request.
         */
        this.isLoading = false;
        this.promoVouchers = [];
        this.filteredPromoVouchers = [];
        this.viewPromoVouchers = [];
        this.currentPage = 1;

        return of([]);
      }),
    );
  }

  /* =========================================================
     KPI helpers
     ========================================================= */

  get activePromoCount(): number {
    return this.promoVouchers.filter((voucher: any) => voucher.is_active)
      .length;
  }

  get inactivePromoCount(): number {
    return this.promoVouchers.filter((voucher: any) => !voucher.is_active)
      .length;
  }

  get averageDiscountLabel(): string {
    if (!this.promoVouchers.length) {
      return "0";
    }

    const values = this.promoVouchers
      .map((voucher: any) => Number(voucher.discount_value))
      .filter((value) => Number.isFinite(value));

    if (!values.length) {
      return "0";
    }

    const average =
      values.reduce((sum, value) => sum + value, 0) / values.length;

    return `${Math.round(average)} avg`;
  }

  /* =========================================================
     Search
     ========================================================= */

  onSearch(searchValue: string): void {
    this.searchTerm = searchValue || "";

    this.applySearchFilter();
  }

  private applySearchFilter(resetPage = true): void {
    const term = this.searchTerm.toLowerCase().trim();

    this.filteredPromoVouchers = term
      ? this.promoVouchers.filter((voucher: any) => {
          const name = String(voucher.name ?? "").toLowerCase();

          const code = String(voucher.code ?? "").toLowerCase();

          const type = String(voucher.discount_type ?? "").toLowerCase();

          const serviceName = String(voucher.service_name ?? "").toLowerCase();

          const salonName = this.getPromotionSalonName(voucher).toLowerCase();

          return (
            name.includes(term) ||
            code.includes(term) ||
            type.includes(term) ||
            serviceName.includes(term) ||
            salonName.includes(term)
          );
        })
      : [...this.promoVouchers];

    if (resetPage) {
      this.currentPage = 1;
    }

    this.updatePaginatedPromos();
  }

  /* =========================================================
     Pagination
     ========================================================= */

  updatePaginatedPromos(): void {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;

    const endIndex = startIndex + this.itemsPerPage;

    this.viewPromoVouchers = this.filteredPromoVouchers.slice(
      startIndex,
      endIndex,
    );
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.updatePaginatedPromos();
  }

  onItemsPerPageChange(limit: number): void {
    this.itemsPerPage = limit;
    this.currentPage = 1;
    this.updatePaginatedPromos();
  }

  onclose(): void {
    this.getPromoVouchers().pipe(takeUntil(this.destroy$)).subscribe();
  }

  /* =========================================================
     Display helpers
     ========================================================= */

  trackByVoucherID(index: number, item: any): string | number {
    return item.promotion_id || item.id || index;
  }

  getPromoInitial(voucher: any): string {
    return (voucher.name || voucher.code || "P").charAt(0).toUpperCase();
  }

  getPromotionSalonName(voucher: any): string {
    const directName =
      voucher?.salon_name ?? voucher?.salon?.name ?? voucher?.salonName;

    if (directName) {
      return String(directName);
    }

    const salonId = String(voucher?.salon_id ?? voucher?.salon ?? "");

    if (!salonId) {
      return "All / Unassigned";
    }

    const salon = this.salonContext
      .salons()
      .find((item: any) => String(item.salon_id) === salonId);

    return salon?.name || "Selected Salon";
  }

  formatDiscountType(type: string): string {
    const normalized = String(type || "")
      .trim()
      .toUpperCase();

    if (normalized === "PERCENT") {
      return "Percentage";
    }

    if (normalized === "FIXED") {
      return "Fixed Amount";
    }

    return type || "Discount";
  }

  formatDiscountValue(voucher: any): string {
    const value = Number(voucher.discount_value);

    if (String(voucher.discount_type || "").toUpperCase() === "PERCENT") {
      return `${Number.isFinite(value) ? value : 0}%`;
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Number.isFinite(value) ? value : 0);
  }

  formatDate(date: string): string {
    if (!date || !dayjs(date).isValid()) {
      return "";
    }

    return dayjs(date).format("MMM D, YYYY");
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
